import axiosClient from "utils/axios";
import { ApiList, ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  AmcVisit,
  CompleteAmcVisitRequest,
  CreateAmcVisitRequest,
  PatchAmcVisitRequest,
} from "features/AmcVisitLogs/types/amc-visit.types";

const AMC_VISITS_BASE = "/amc-visits";

/** Query shape for GET /amc-visits (paginated list + filters). */
export type AmcVisitLogListParams = {
  page?: number;
  limit?: number;
  search?: string;
  /** AMC contract id — limits list to one contract. */
  amcId?: number;
  /** Comma-separated AmcVisitStatus values, e.g. SCHEDULED,COMPLETED */
  status?: string;
  /** Comma-separated parent contract AmcStatus (join on contract). */
  amcStatus?: string;
  /** Exact visit number on the contract. */
  visitNumber?: number;
  /** Partial match on visit address. */
  address?: string;
  clientId?: number;
  amcCode?: string;
  city?: string;
  state?: string;
  scheduledAtFrom?: string;
  scheduledAtTo?: string;
  completedFrom?: string;
  completedTo?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  expiredFrom?: string;
  expiredTo?: string;
};

function buildVisitLogListQueryParams(
  input: AmcVisitLogListParams,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  const setPositiveInt = (key: string, v: number | undefined) => {
    if (v != null && Number.isFinite(v) && Number(v) > 0) {
      params[key] = Number(v);
    }
  };
  const setNonEmpty = (key: string, v: string | undefined) => {
    if (v != null && String(v).trim() !== "") {
      params[key] = String(v).trim();
    }
  };

  if (input.page != null) params.page = input.page;
  if (input.limit != null) params.limit = input.limit;
  setNonEmpty("search", input.search);
  setPositiveInt("amcId", input.amcId);
  setNonEmpty("status", input.status);
  setNonEmpty("amcStatus", input.amcStatus);
  setPositiveInt("visitNumber", input.visitNumber);
  setNonEmpty("address", input.address);
  setPositiveInt("clientId", input.clientId);
  setNonEmpty("amcCode", input.amcCode);
  setNonEmpty("city", input.city);
  setNonEmpty("state", input.state);
  setNonEmpty("scheduledAtFrom", input.scheduledAtFrom);
  setNonEmpty("scheduledAtTo", input.scheduledAtTo);
  setNonEmpty("completedFrom", input.completedFrom);
  setNonEmpty("completedTo", input.completedTo);
  setNonEmpty("createdFrom", input.createdFrom);
  setNonEmpty("createdTo", input.createdTo);
  setNonEmpty("updatedFrom", input.updatedFrom);
  setNonEmpty("updatedTo", input.updatedTo);
  setNonEmpty("expiredFrom", input.expiredFrom);
  setNonEmpty("expiredTo", input.expiredTo);

  return params;
}

export const getAmcVisits = async (input: AmcVisitLogListParams = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<AmcVisit[]>>>(
      AMC_VISITS_BASE,
      { params: buildVisitLogListQueryParams(input) },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Visits  : ", err);
    throw err;
  }
};

export const createAmcVisit = async (payload: CreateAmcVisitRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<AmcVisit>>(
      AMC_VISITS_BASE,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating AmcVisit : ", err);
    throw err;
  }
};

export const getAmcVisitById = async (visitId: number) => {
  try {
    if (!Number.isInteger(visitId) || visitId <= 0) {
      throw new Error("Invalid visit id");
    }
    const response = await axiosClient.get<ApiResponse<AmcVisit>>(
      `${AMC_VISITS_BASE}/${visitId}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching AmcVisit by id : ", err);
    throw err;
  }
};

export const updateAmcVisit = async (
  visitId: number,
  payload: PatchAmcVisitRequest,
) => {
  try {
    if (!Number.isInteger(visitId) || visitId <= 0) {
      throw new Error("Invalid visit id");
    }
    const response = await axiosClient.patch<ApiResponse<AmcVisit>>(
      `${AMC_VISITS_BASE}/${visitId}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating AmcVisit : ", err);
    throw err;
  }
};

export const startAmcVisit = async (visitId: number) => {
  try {
    if (!Number.isInteger(visitId) || visitId <= 0) {
      throw new Error("Invalid visit id");
    }
    const response = await axiosClient.post<ApiResponse<AmcVisit>>(
      `${AMC_VISITS_BASE}/${visitId}/start`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error starting AmcVisit : ", err);
    throw err;
  }
};

export const cancelAmcVisit = async (visitId: number, reason?: string) => {
  try {
    if (!Number.isInteger(visitId) || visitId <= 0) {
      throw new Error("Invalid visit id");
    }
    const response = await axiosClient.post<ApiResponse<AmcVisit>>(
      `${AMC_VISITS_BASE}/${visitId}/cancel`,
      reason?.trim() ? { reason: reason.trim() } : {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling visit : ", err);
    throw err;
  }
};

function compactCompleteBody(
  body: CompleteAmcVisitRequest | undefined,
): Record<string, unknown> {
  if (!body) return {};
  const out: Record<string, unknown> = {};
  const remarks = body.completionRemarks != null ? String(body.completionRemarks).trim() : "";
  if (remarks) out.completionRemarks = remarks;
  return out;
}

export const completeAmcVisit = async (
  visitId: number,
  body?: CompleteAmcVisitRequest,
) => {
  try {
    if (!Number.isInteger(visitId) || visitId <= 0) {
      throw new Error("Invalid visit id");
    }
    const payload = compactCompleteBody(body);
    const response = await axiosClient.post<ApiResponse<AmcVisit>>(
      `${AMC_VISITS_BASE}/${visitId}/complete`,
      Object.keys(payload).length ? payload : {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error completing visit : ", err);
    throw err;
  }
};

export const getAmcVisitOptions = async ({
  query,
  limit,
  page,
  forType,
}: {
  query?: string;
  limit?: number;
  page?: number;
  forType: "QUOTATION" | "WORK_ORDER";
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      "/amc-visits/options",
      {
        params: {
          query,
          limit,
          page,
          forType,
        },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching Amc Visit Options : ", err);
    throw err;
  }
};

export interface AmcVisitSummary {
  windowDays: number;
  total: number;
  today: number;
  thisWeek: number;
  overdue: number;
  byStatus: Record<string, number>;
}

export const getAmcVisitSummary = async (days?: number) => {
  try {
    const response = await axiosClient.get<ApiResponse<AmcVisitSummary>>(
      `${AMC_VISITS_BASE}/summary`,
      { params: days != null ? { days } : undefined },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching AMC visit summary : ", err);
    throw err;
  }
};
